import { unwrapEnvelope } from "@/lib/api";
import { parseBuySourcesSearchResponse } from "@/lib/buySources/parseSearchResponse";
import {
  BUY_SOURCES_STATUS,
  classifyBuySourcesResult,
} from "@/lib/storefrontHunter/autoBuySources";
import {
  isProviderUnavailableResult,
  providerUnavailableDetail,
} from "@/lib/storefrontHunter/buySourcesFailures";

/**
 * Parse POST /buy-sources/search-batch envelope into per-item rows.
 * Success statuses reuse ``classifyBuySourcesResult`` (same as single search).
 * ``status=error`` stays error — never remapped to no_match.
 *
 * @returns {{ batch_id: string|null, results: Array<{ walmart_item_id: string|null, status: string, parsed: object|null, error: object|null }> }}
 */
export function parseBuySourcesBatchResponse(envelopeOrData) {
  const data = unwrapEnvelope(envelopeOrData) || {};
  const rows = Array.isArray(data.results) ? data.results : [];
  const results = rows.map((row) => {
    const wid = row?.walmart_item_id != null ? String(row.walmart_item_id) : null;
    const rawStatus = String(row?.status || "").toLowerCase();

    // Explicit error from batch item — do not inspect result payload as success.
    if (rawStatus === "error" || row?.error || !row?.result) {
      return {
        walmart_item_id: wid,
        status: BUY_SOURCES_STATUS.ERROR,
        parsed: null,
        error:
          row?.error || {
            message: "Buy Sources search failed.",
            reason: "buy_sources_search_failed",
          },
      };
    }

    if (isProviderUnavailableResult(row.result)) {
      return {
        walmart_item_id: wid,
        status: BUY_SOURCES_STATUS.ERROR,
        parsed: null,
        error: providerUnavailableDetail({ walmart_item_id: wid, ...row.result }),
      };
    }

    const parsed = parseBuySourcesSearchResponse(row.result);
    return {
      walmart_item_id: wid || parsed.walmart_item_id || null,
      status: classifyBuySourcesResult(parsed),
      parsed,
      error: null,
    };
  });
  return {
    batch_id: data.batch_id || null,
    results,
    received: data.received ?? results.length,
    unique: data.unique ?? results.length,
    counts: data.counts || null,
    batch_max_items: data.batch_max_items ?? null,
  };
}
